import Style from 'ol/style/Style';
import { VectorMath } from 'shared/common/domain/vectorMath';
import { Color } from 'shared/common/domain/color';
import { lockIcon, messageIcon, warningIcon, type IconResource } from '../domain/icons';
import { parseRichText } from '../../mapStyles/domain/richText';

export const rectangleStyle = new Style({
  renderer: (coordinates, state) => {
    const tmp = coordinates[0] as number[][];
    const properties = state.feature.getProperties();
    const mode = properties['mode'];

    if (mode !== 'rectangle') return;

    if (tmp.length < 4) return;

    const p0 = tmp[0] as number[];
    const p1 = tmp[1] as number[];
    const p2 = tmp[2] as number[];
    const p3 = tmp[3] as number[];

    const widthVector = VectorMath.subtract(p1, p0);
    const maxWidth = VectorMath.length(widthVector);
    const halfWidthVector = VectorMath.multiply(widthVector, 0.5);

    const heightVector = VectorMath.subtract(p2, p1);
    const maxHeight = VectorMath.length(heightVector);
    const halfHeightVector = VectorMath.multiply(heightVector, 0.5);
    const unitHeightVector = VectorMath.unit(heightVector);

    const centerPoint = VectorMath.add(VectorMath.add(p0, halfHeightVector), halfWidthVector);
    const rightCenterPoint = VectorMath.add(centerPoint, halfWidthVector);
    const leftCenterPoint = VectorMath.subtract(centerPoint, halfWidthVector);

    const unitWidthVector = VectorMath.unit(widthVector);
    let rotation = Math.atan2(unitWidthVector[1]!, unitWidthVector[0]!);

    const camerVector = VectorMath.rotate([1, 0], -state.rotation);
    const projectedLength = VectorMath.dot(camerVector, unitWidthVector);

    const opacity = properties['opacity'];
    let label = properties['label'];
    let color = Color.setAlpha(properties['color'], opacity);
    const outlineColor = Color.setAlpha(properties['outlineColor'], opacity);
    const outlineSize = properties['outlineSize'];
    const textColor = Color.setAlpha(properties['textColor'], opacity);
    const messageIconVisible = properties['messageIconVisible'] as boolean;

    const baseFontSize = properties['fontSize'] || 12;
    let scaledFontSize = baseFontSize / state.resolution;

    const minFontSizeText = 5;
    const minFontSizeIcon = 5;

    const upsideDown = projectedLength < 0;

    if (upsideDown) {
      rotation += Math.PI;
    }

    const ctx = state.context;

    ctx.save();

    ctx.fillStyle = color;
    ctx.strokeStyle = outlineColor;
    ctx.lineWidth = outlineSize;

    ctx.beginPath();
    ctx.moveTo(p0[0]!, p0[1]!);
    ctx.lineTo(p1[0]!, p1[1]!);
    ctx.lineTo(p2[0]!, p2[1]!);
    ctx.lineTo(p3[0]!, p3[1]!);
    ctx.lineTo(p0[0]!, p0[1]!);
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    const icons: IconResource[] = [];

    if (messageIconVisible && scaledFontSize >= minFontSizeIcon) {
      icons.push(messageIcon);
    }

    const iconMargin = 2;

    let iconPoint = upsideDown ? rightCenterPoint : leftCenterPoint;
    let contentHorizontalVector = upsideDown
      ? unitWidthVector
      : VectorMath.multiply(unitWidthVector, -1);
    let contentVerticalVector = upsideDown
      ? unitHeightVector
      : VectorMath.multiply(unitHeightVector, -1);

    let remainingWidth = maxWidth - iconMargin;
    iconPoint = VectorMath.add(
      iconPoint,
      VectorMath.multiply(contentHorizontalVector, -iconMargin),
    );
    let iconAreaWidth = icons.length > 0 ? iconMargin : 0;

    for (const icon of icons) {
      let scale = scaledFontSize / icon.height;

      // Maximum scale that still fits inside the rectangle
      const maxScaleX = remainingWidth / icon.width;
      const maxScaleY = maxHeight / icon.height;

      // Scale so the icon always fits
      scale = Math.min(scale, maxScaleX, maxScaleY);

      ctx.save();
      const drawPoint = VectorMath.add(
        iconPoint,
        VectorMath.multiply(contentVerticalVector, (scale * icon.height) / 2),
      );
      ctx.translate(drawPoint[0]!, drawPoint[1]!);
      ctx.scale(scale, scale);
      ctx.rotate(rotation);
      ctx.fillStyle = textColor;

      ctx.fill(icon.path);
      ctx.restore();
      remainingWidth -= icon.width * scale;
      iconAreaWidth += icon.width * scale;
      iconPoint = VectorMath.add(
        iconPoint,
        VectorMath.multiply(contentHorizontalVector, -scale * icon.width),
      );
    }

    const textCenterPoint = VectorMath.add(
      centerPoint,
      VectorMath.multiply(contentHorizontalVector, -iconAreaWidth / 2),
    );

    if (label) {
      let fontSize = scaledFontSize;
      let fontWeight = 400;
      const fontFamily = 'sans-serif';

      const buildFont = () => `${fontWeight} ${fontSize}px ${fontFamily}`;

      const richText = parseRichText(label);

      ctx.save();
      ctx.textBaseline = 'middle';
      ctx.fillStyle = textColor;

      let width = 0;
      let height = 0;

      const measure = () => {
        width = 0;
        height = 0;

        fontWeight = 400;
        ctx.font = buildFont();

        for (const token of richText.tokens) {
          switch (token.type) {
            case 'bold-start':
              fontWeight = 700;
              ctx.font = buildFont();
              break;

            case 'bold-end':
              fontWeight = 400;
              ctx.font = buildFont();
              break;

            case 'text': {
              const m = ctx.measureText(token.content);
              const tokenHeight =
                (m.fontBoundingBoxAscent ?? fontSize) + (m.fontBoundingBoxDescent ?? 0);

              height = Math.max(height, tokenHeight);
              width += m.width;
              break;
            }
          }
        }
      };

      measure();

      const scale = Math.min(remainingWidth / width, maxHeight / height, 1);

      fontSize *= scale;

      // Do not render if text becomes less than minFontSizeText
      if (fontSize < minFontSizeText) {
        ctx.restore();
        return;
      }

      ctx.font = buildFont();

      const textStartPoint = VectorMath.add(
        textCenterPoint,
        VectorMath.multiply(contentHorizontalVector, (width * scale) / 2),
      );

      ctx.translate(textStartPoint[0]!, textStartPoint[1]!);
      ctx.rotate(rotation);

      let x = 0;

      for (const token of richText.tokens) {
        switch (token.type) {
          case 'bold-start':
            fontWeight = 700;
            ctx.font = buildFont();
            break;

          case 'bold-end':
            fontWeight = 400;
            ctx.font = buildFont();
            break;

          case 'text':
            ctx.fillText(token.content, x, 0);
            x += ctx.measureText(token.content).width;
            break;
        }
      }

      ctx.restore();
    }
  },
});
